import type { SceneNode } from "../scene/model";
import { changeEffect, effects, type NativeEffects } from "./effects";

export type ShadowStyle = NonNullable<NativeEffects["shadowStyle"]>;
export type ShadowField = "color" | "alpha" | "blur" | "distance" | "angle";
/** New shadows start neutral; old implicit styles are materialized before disabling. */
export function enableShadow(nodes: readonly SceneNode[], enabled: boolean) {
  return nodes.map((node) => {
    const e = effects(node);
    const style =
      e.shadowStyle ??
      (e.shadow
        ? shadowStyle(node)
        : enabled
          ? { color: "#0f172a", alpha: 1, blur: 0, x: 0, y: 0, angle: 0 }
          : undefined);
    return {
      ...node,
      effects: {
        ...node.effects,
        shadow: enabled,
        ...(style ? { shadowStyle: style } : {}),
      },
    };
  });
}
export function shadowStyle(node: SceneNode): ShadowStyle {
  return (
    effects(node).shadowStyle ?? {
      color: "#0f172a",
      alpha: 0.32,
      blur: 12,
      x: 5,
      y: 5,
    }
  );
}
export function shadowDistance(s: ShadowStyle) {
  return Math.hypot(s.x, s.y);
}
export function shadowAngle(s: ShadowStyle) {
  return shadowDistance(s) > 1e-8
    ? ((Math.atan2(s.y, s.x) * 180) / Math.PI + 360) % 360
    : (s.angle ?? 45);
}
/** Preserve each object's independent channels, including imported shadow offsets. */
export function changeShadow(
  nodes: readonly SceneNode[],
  field: ShadowField,
  value: string | number,
) {
  if (
    field === "color"
      ? typeof value !== "string" || !/^#[\da-f]{6}$/i.test(value)
      : typeof value !== "number" || !Number.isFinite(value)
  )
    throw new Error("Некорректное значение тени.");
  return nodes.map((node) => {
    const s = { ...shadowStyle(node) };
    if (field === "color") s.color = String(value).toLowerCase();
    else {
      const n = Number(value),
        clamp = (max: number) => Math.max(0, Math.min(max, n));
      if (field === "alpha") s.alpha = clamp(100) / 100;
      else if (field === "blur") s.blur = clamp(200);
      else {
        const angle = field === "angle" ? clamp(360) : shadowAngle(s),
          radians = (angle * Math.PI) / 180,
          cos = Math.cos(radians),
          sin = Math.sin(radians),
          requested = field === "distance" ? clamp(1000) : shadowDistance(s),
          distance = Math.min(
            requested,
            1000 / Math.max(Math.abs(cos), Math.abs(sin)),
          );
        s.x = Math.abs(cos) < 1e-12 ? 0 : distance * cos;
        s.y = Math.abs(sin) < 1e-12 ? 0 : distance * sin;
        // Keep the chosen direction when distance is zero, including save/reopen.
        s.angle = angle;
      }
    }
    return changeEffect([node], "shadowStyle", s)[0]!;
  });
}
